import type { GolfServiceLocation, GolfServiceOption } from '@/scopes/checkout/domain/types'
import type { APIBookingDraftGolfServiceOption } from '@/scopes/checkout/infrastructure/api/types/services/golf'
import { toSnakeCase } from '@/helpers/string-helpers'

export interface GolfServiceOptionMapper {
  buildLocationId: (location: string) => string
  mapOptions: (options: APIBookingDraftGolfServiceOption[]) => Record<string, GolfServiceOption>
  mapLocations: (options: APIBookingDraftGolfServiceOption[]) => Record<string, GolfServiceLocation>
}

export function createGolfServiceOptionMapper(): GolfServiceOptionMapper {
  function buildLocationId(location: string): string {
    return toSnakeCase(location)
  }

  function mapOptions(options: APIBookingDraftGolfServiceOption[]): Record<string, GolfServiceOption> {
    return options.reduce(
      (acc, option) => {
        const id = option.id
        return {
          ...acc,
          [id]: {
            id,
            code: option.code,
            locationId: buildLocationId(option.location),
            holes: Number(option.holes),
            withBuggy: option.hasBuggy,
            isBuggyEligible: false,
            date: option.date,
            time: option.time,
            price: option.price,
            label: option.displayText,
            isNoneOption: false,
            appliesToParticipants: option.appliesToParticipants.map(pid => pid.toString()),
            buggyOccupancy: option.occupancy,
          },
        }
      },
      {} as Record<string, GolfServiceOption>,
    )
  }

  function mapLocations(options: APIBookingDraftGolfServiceOption[]): Record<string, GolfServiceLocation> {
    return options.reduce(
      (acc, option) => {
        const locationId = buildLocationId(option.location)
        const existing = acc[locationId]
        if (existing) {
          if (!existing.availableHoles.includes(Number(option.holes))) {
            existing.availableHoles.push(Number(option.holes))
          }
          if (!existing.availableDates.includes(option.date)) {
            existing.availableDates.push(option.date)
            existing.availableDates.sort()
          }
          option.appliesToParticipants.forEach((pid) => {
            const pidStr = pid.toString()
            if (!existing.eligibleParticipantIds.includes(pidStr)) {
              existing.eligibleParticipantIds.push(pidStr)
            }
          })
          if (option.hasBuggy) {
            existing.isBuggyEligible = true
          }
          return acc
        }

        return {
          ...acc,
          [locationId]: {
            id: locationId,
            name: option.location,
            availableHoles: [Number(option.holes)],
            isBuggyEligible: option.hasBuggy,
            availableDates: [option.date],
            eligibleParticipantIds: option.appliesToParticipants.map(pid => pid.toString()),
          },
        }
      },
      {} as Record<string, GolfServiceLocation>,
    )
  }

  return { buildLocationId, mapOptions, mapLocations }
}
